import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase'; // Added import for db
import { doc, getDoc, updateDoc } from 'firebase/firestore'; // Added import for doc, getDoc, and updateDoc
import { collection, getDocs, query, where } from 'firebase/firestore'; // Added import for collection, getDocs, query, and where
import { getEnvironmentConfig } from '../lib/environment';

export function useOnboardingStatus() {
  const { currentUser } = useAuth();
  
  const [checklistData, setChecklistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock data for development
  const getMockChecklistData = () => ({
    steps: {
      setup_fee: { status: 'notStarted' },
      identity: { status: 'notStarted' },
      payment: { status: 'notStarted' },
      documents: { status: 'notStarted' },
      product: { status: 'notStarted' },
      store: { status: 'notStarted' }
    }
  });

  // Check if user has paid setup fee
  const checkSetupFeeStatus = useCallback(async () => {
    if (!currentUser) {
      return 'notStarted';
    }
    
    try {
      // First check if user already has a store (which means they've completed onboarding)
      const shopsRef = collection(db, 'shops');
      const q = query(shopsRef, where('sellerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        return 'completed';
      }
      
      // Check if user has a setup fee payment record
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Check if user has completed setup fee payment
        if (userData.setupFeePaid) {
          return 'completed';
        }
        // Check if user has started onboarding (indicates they've seen the setup fee page)
        if (userData.onboardingStarted) {
          return 'inProgress';
        }
      }
      return 'notStarted';
    } catch (error) {
      console.error('Error checking setup fee status:', error);
      return 'notStarted';
    }
  }, [currentUser]);

  // Check if user has products
  const checkProductStatus = useCallback(async () => {
    if (!currentUser) {
      return 'notStarted';
    }
    
    try {
      // Check if user has products in the products collection
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('sellerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        return 'completed';
      }
      
      return 'notStarted';
    } catch (error) {
      console.error('Error checking product status:', error);
      return 'notStarted';
    }
  }, [currentUser]);

  // Check if documents are required based on food type settings
  const checkDocumentsStatus = useCallback(async () => {
    if (!currentUser) {
      return 'notStarted';
    }
    
    try {
      // Check if user has a store and what food types they sell
      const shopsRef = collection(db, 'shops');
      const q = query(shopsRef, where('sellerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // No store found, documents not required yet
        return 'notStarted';
      }
      
      const storeData = querySnapshot.docs[0].data();
      const sellsNonPerishables = storeData.sellsNonPerishables || false;
      const sellsPerishables = storeData.sellsPerishables || false;
      
      // If seller only sells non-perishables, documents are not required
      if (sellsNonPerishables && !sellsPerishables) {
        return 'completed';
      }
      
      // If seller sells perishables (alone or with non-perishables), documents are required
      if (sellsPerishables) {
        return 'notStarted';
      }
      
      // Default case: documents not required
      return 'completed';
    } catch (error) {
      console.error('Error checking documents status:', error);
      return 'notStarted';
    }
  }, [currentUser]);

  // Check identity verification status
  const checkIdentityStatus = useCallback(async () => {
    if (!currentUser) {
      return 'notStarted';
    }
    
    try {
      // First check local user document for quick response
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // If we have a stored verification status, use it
        if (userData.identityVerified) {
          return 'completed';
        }
        
        // If user has started verification but not completed, check with API
        if (userData.identity_verification_started || userData.stripe_verification_id) {
          try {
            // Call the Cloud Function to get real-time status
            const { apiBaseUrl } = getEnvironmentConfig();
            const response = await fetch(`${apiBaseUrl}/getIdentityStatus?userId=${currentUser.uid}`);
            
            if (response.ok) {
              const data = await response.json();
              if (data.status === 'verified') {
                return 'completed';
              } else if (data.status === 'pending' || data.status === 'processing') {
                return 'inProgress';
              } else if (data.status === 'canceled' || data.status === 'requires_action') {
                return 'notStarted';
              }
            }
          } catch (apiError) {
            console.warn('Failed to fetch identity status from API, using local status:', apiError);
            // Fall back to local status
            if (userData.identity_verification_status === 'verified') {
              return 'completed';
            }
            return 'inProgress';
          }
          
          return 'inProgress';
        }
      }
      
      return 'notStarted';
    } catch (error) {
      console.error('Error checking identity status:', error);
      return 'notStarted';
    }
  }, [currentUser]);

  // Check if user has connected a bank account
  const checkPaymentStatus = useCallback(async () => {
    if (!currentUser) {
      return 'notStarted';
    }
    
    try {
      // Check if user has a Financial Connections account ID
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const financialConnectionsAccountId = userData.financialConnectionsAccountId;
        const stripeAccountId = userData.stripeAccountId;
        
        // Check for either financial connections or stripe account
        const hasBankConnection = financialConnectionsAccountId || stripeAccountId;
        
        console.log('checkPaymentStatus: User has financialConnectionsAccountId:', financialConnectionsAccountId);
        console.log('checkPaymentStatus: User has stripeAccountId:', stripeAccountId);
        
        if (!hasBankConnection) {
          console.log('checkPaymentStatus: No bank connection found, returning notStarted');
          return 'notStarted';
        }
        
        // Call the Cloud Function to get real bank connection status
        try {
          const { apiBaseUrl } = getEnvironmentConfig();
          console.log('checkPaymentStatus: Calling API:', `${apiBaseUrl}/getFinancialConnectionsStatus`);
          
          const response = await fetch(`${apiBaseUrl}/getFinancialConnectionsStatus`, {
            headers: {
              'Authorization': `Bearer ${await currentUser.getIdToken()}`
            }
          });
          
          console.log('checkPaymentStatus: API response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('checkPaymentStatus: API response data:', data);
            
            // Map the API status to our frontend status
            // 'connected' from Financial Connections API maps to 'completed'
            if (data.status === 'connected' || data.status === 'verified') {
              console.log('checkPaymentStatus: Status is connected/verified, returning completed');
              return 'completed';
            } else if (data.status === 'pending' || data.status === 'needs_action') {
              console.log('checkPaymentStatus: Status is pending/needs_action, returning inProgress');
              return 'inProgress';
            } else {
              console.log('checkPaymentStatus: Status is not_connected, returning notStarted');
              return 'notStarted';
            }
          } else {
            console.error('checkPaymentStatus: API call failed with status:', response.status);
            // If API fails but we have account ID, assume in progress
            return 'inProgress';
          }
        } catch (apiError) {
          console.warn('Failed to fetch bank connection status from API, using local status:', apiError);
          // Fall back to local check - if they have an account ID, consider it in progress
          console.log('checkPaymentStatus: API failed, falling back to inProgress');
          return 'inProgress';
        }
      }
      
      console.log('checkPaymentStatus: No user document, returning notStarted');
      return 'notStarted';
    } catch (error) {
      console.error('Error checking payment status:', error);
      return 'notStarted';
    }
  }, [currentUser]);

  // Check if user has published their store
  const checkStoreStatus = useCallback(async () => {
    if (!currentUser) {
      return 'notStarted';
    }
    
    try {
      // Check if user has a shop document
      const shopsRef = collection(db, 'shops');
      const q = query(shopsRef, where('sellerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const shopData = querySnapshot.docs[0].data();
        // Check if the store is published (status is 'active')
        if (shopData.status === 'active') {
          return 'completed';
        }
        return 'inProgress';
      }
      
      return 'notStarted';
    } catch (error) {
      console.error('Error checking store status:', error);
      return 'notStarted';
    }
  }, [currentUser]);

  // Check if user can access seller hub (setup fee must be paid OR store must exist)
  const canAccessSellerHub = useCallback(() => {
    if (!checklistData) {
      return false;
    }
    
    const setupFeeStatus = checklistData.steps.setup_fee.status;
    
    // User can access if setup fee is completed OR they have an existing store
    return setupFeeStatus === 'completed';
  }, [checklistData]);

  // Check if user can access seller hub (setup fee must be paid OR store must exist) - alternative method
  const canAccessSellerHubDirect = useCallback(async () => {
    if (!currentUser) return false;
    
    try {
      // First check if user already has a store
      const shopsRef = collection(db, 'shops');
      const q = query(shopsRef, where('sellerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        return true;
      }
      
      // If no store exists, check setup fee payment
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const hasPaid = userData.setupFeePaid === true;
        return hasPaid;
      }
      return false;
    } catch (error) {
      console.error('Error checking setup fee status directly:', error);
      return false;
    }
  }, [currentUser]);

  // Fetch onboarding status
  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser) {
        const mockData = getMockChecklistData();
        setChecklistData(mockData);
        setLoading(false);
        return;
      }

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('fetchStatus timeout')), 10000);
      });

      const statusPromise = Promise.all([
        checkSetupFeeStatus(),
        checkIdentityStatus(),
        checkProductStatus(),
        checkDocumentsStatus(),
        checkPaymentStatus(),
        checkStoreStatus()
      ]);

      const [setupFeeStatus, identityStatus, productStatus, documentsStatus, paymentStatus, storeStatus] = 
        await Promise.race([statusPromise, timeoutPromise]);
      
      const mockData = getMockChecklistData();
      mockData.steps.setup_fee.status = setupFeeStatus;
      
      // Map identity status to frontend status
      if (identityStatus === 'verified') {
        mockData.steps.identity.status = 'completed';
      } else {
        mockData.steps.identity.status = identityStatus;
      }
      
      // Set product status
      mockData.steps.product.status = productStatus;
      
      // Set documents status
      mockData.steps.documents.status = documentsStatus;
      
      // Set payment status
      mockData.steps.payment.status = paymentStatus;
      
      // Set store status
      mockData.steps.store.status = storeStatus;
      
      setChecklistData(mockData);
      
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      setError(err.message);
      setChecklistData(getMockChecklistData());
    } finally {
      setLoading(false);
    }
  }, [currentUser, checkSetupFeeStatus, checkIdentityStatus, checkProductStatus, checkDocumentsStatus, checkPaymentStatus, checkStoreStatus]);

  // Update step status (mock implementation for development)
  const updateStepStatus = useCallback(async (step, status, metadata = null) => {
    try {
      // For development, just update the local state
      setChecklistData(prev => {
        if (!prev) return prev;
        
        const updated = {
          ...prev,
          steps: {
            ...prev.steps,
            [step]: {
              ...prev.steps[step],
              status,
              updatedAt: new Date().toISOString()
            }
          }
        };
        
        // Recalculate progress
        const completedSteps = Object.values(updated.steps).filter(s => s.status === 'completed').length;
        updated.overallProgress = Math.round((completedSteps / Object.keys(updated.steps).length) * 100);
        
        return updated;
      });
      
      return true;
    } catch (err) {
      console.error('Error updating step status:', err);
      return false;
    }
  }, []);

  // Mark step as completed
  const completeStep = useCallback(async (step, metadata = null) => {
    return updateStepStatus(step, 'completed', metadata);
  }, [updateStepStatus]);

  // Mark step as in progress
  const startStep = useCallback(async (step, metadata = null) => {
    return updateStepStatus(step, 'inProgress', metadata);
  }, [updateStepStatus]);

  // Reset step to not started
  const resetStep = useCallback(async (step) => {
    return updateStepStatus(step, 'notStarted');
  }, [updateStepStatus]);

  // Get status of a specific step
  const getStepStatus = useCallback((stepId) => {
    if (!checklistData || !checklistData.steps) {
      return 'notStarted';
    }
    
    const step = checklistData.steps[stepId];
    if (!step) {
      return 'notStarted';
    }
    
    return step.status;
  }, [checklistData]);

  // Get overall progress
  const getOverallProgress = useCallback(() => {
    if (!checklistData) return 0;
    
    // Setup fee must be completed first
    if (checklistData.steps.setup_fee.status !== 'completed') {
      return 0;
    }
    
    // Only count other steps if setup fee is completed
    const otherSteps = Object.entries(checklistData.steps).filter(([key, step]) => key !== 'setup_fee');
    const completedOtherSteps = otherSteps.filter(([key, step]) => step.status === 'completed').length;
    
    // Calculate progress: setup fee (1 step) + other steps
    const totalSteps = 1 + otherSteps.length;
    const completedSteps = 1 + completedOtherSteps; // Setup fee always counts as 1
    
    return Math.round((completedSteps / totalSteps) * 100);
  }, [checklistData]);

  // Check if all steps are completed
  const isComplete = useCallback(() => {
    if (!checklistData) return false;
    
    // Setup fee must be completed first
    if (checklistData.steps.setup_fee.status !== 'completed') {
      return false;
    }
    
    // Check if all other steps are completed
    const otherSteps = Object.entries(checklistData.steps).filter(([key, step]) => key !== 'setup_fee');
    return otherSteps.every(([key, step]) => step.status === 'completed');
  }, [checklistData]);

  // Mark setup fee as completed (for testing purposes)
  const markSetupFeeCompleted = useCallback(async () => {
    if (!currentUser) return false;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        setupFeePaid: true,
        setupFeePaidAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      // Refresh the status
      await fetchStatus();
      return true;
    } catch (error) {
      console.error('Error marking setup fee as completed:', error);
      return false;
    }
  }, [currentUser, fetchStatus]);

  // Mark setup fee as completed for users with existing stores
  const markSetupFeeCompletedForExistingStore = useCallback(async () => {
    if (!currentUser) {
      return false;
    }
    
    try {
      // Check if user has an existing store
      const shopsRef = collection(db, 'shops');
      const q = query(shopsRef, where('sellerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update user profile to mark setup fee as completed
        await updateDoc(doc(db, 'users', currentUser.uid), {
          setupFeePaid: true,
          setupFeePaidAt: new Date().toISOString(),
          onboardingStarted: true,
          updatedAt: new Date().toISOString()
        });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking setup fee as completed for existing store:', error);
      return false;
    }
  }, [currentUser]);

  // Mark identity verification as started
  const startIdentityVerification = useCallback(async () => {
    if (!currentUser) return false;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        identity_verification_started: true,
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Error starting identity verification:', error);
      return false;
    }
  }, [currentUser]);

  // Mark identity verification as completed
  const completeIdentityVerification = useCallback(async (status = 'verified') => {
    if (!currentUser) return false;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        identity_verification_status: status,
        identity_verification_completed_at: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      // Mark the identity step as completed in local checklist data
      if (status === 'verified') {
        await completeStep('identity', {
          completedAt: new Date().toISOString(),
          stripeVerificationId: null // Will be filled by the webhook
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error completing identity verification:', error);
      return false;
    }
  }, [currentUser, completeStep]);

  // Debug function to check current user's setup fee status
  const debugSetupFeeStatus = useCallback(async () => {
    if (!currentUser) {
      return null;
    }
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData;
      } else {
        return null;
      }
    } catch (error) {
      console.error('debugSetupFeeStatus: Error:', error);
      return null;
    }
  }, [currentUser]);

  // Initial fetch - only once per user
  const [hasInitialized, setHasInitialized] = useState(false);
  
  useEffect(() => {
    if (currentUser && !hasInitialized) {
      setHasInitialized(true);
      // Use setTimeout to defer the fetch and improve perceived performance
      setTimeout(() => {
        fetchStatus().catch(err => {
          console.error('Error in initial fetchStatus:', err);
          setLoading(false);
        });
      }, 100);
    }
  }, [currentUser, hasInitialized, fetchStatus]);

  // Add periodic refresh for identity verification and bank account status
  useEffect(() => {
    if (!currentUser || !hasInitialized) {
      return;
    }

    // Set up interval to refresh status every 30 seconds
    const refreshInterval = setInterval(() => {
      // Only refresh if we have checklist data and either identity verification or bank account is in progress
      if (checklistData && (
        checklistData.steps?.identity?.status === 'inProgress' ||
        checklistData.steps?.payment?.status === 'inProgress'
      )) {
        console.log('Auto-refreshing verification and bank account status...');
        fetchStatus().catch(err => {
          console.error('Error in periodic refresh:', err);
        });
      }
    }, 30000); // 30 seconds

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(refreshInterval);
    };
  }, [currentUser, hasInitialized, checklistData, fetchStatus]);

  return {
    checklistData,
    loading,
    error,
    fetchStatus,
    updateStepStatus,
    completeStep,
    startStep,
    resetStep,
    getStepStatus,
    getOverallProgress,
    isComplete,
    markSetupFeeCompleted,
    markSetupFeeCompletedForExistingStore,
    startIdentityVerification,
    completeIdentityVerification,
    canAccessSellerHub,
    canAccessSellerHubDirect,
    debugSetupFeeStatus
  };
}
